<template>
  <div>
    <HeaderBreadcrumbs
      :items="[
        { label: 'Publisher', to: '/vendor' },
        { label: item?.name }
      ]"
      :title="item?.name"
    />

    <div class="w-full p-4 my-3 bg-white rounded-lg shadow-sm">

      <div class="flex items-center justify-between">
        <h4 class="text-xl font-bold text-gray-900">Publisher Details</h4>
        <Button @click="$router.push(`/vendor/edit/${$route.params.id}`)">
          <template #iconBefore>
            <EditFilled color="white" className="w-4 h-4" />
          </template>
          Edit
        </Button>
      </div>

      <div class="flex mt-2">
        <div class="flex-col w-1/2">
          <DescriptionList>
            <DescriptionTerm>Publisher Image</DescriptionTerm>

            <DescriptionDetail withSlotLoading :isLoading="isLoadingVendor" className="mt-2">
              <template #slotLoading>
                <Skeleton className="rounded-md" style="height: 144px;width: 144px;" />
              </template>
              <img
                v-if="!!item?.icon_image_normal"
                alt="Vendor Image"
                class="form-control-file"
                height="144"
                width="144"
                style="border:1px solid #ccc;"
                :src="imageSrc"
                @error="handleImageError($event)"
              />
              <img
                v-else
                class="form-control-file"
                style="border:1px solid #ccc;"
                height="144"
                width="144"
                :src="imagePath"
                alt="Vendor Image"
              />
            </DescriptionDetail>

            <DescriptionTerm className="gd-dt">Publisher Name</DescriptionTerm>
            <DescriptionDetail :isLoading="isLoadingVendor">{{ item?.name || '-' }}</DescriptionDetail>

            <DescriptionTerm className="gd-dt">Slug</DescriptionTerm>
            <DescriptionDetail :isLoading="isLoadingVendor">{{ item?.slug || '-' }}</DescriptionDetail>

            <DescriptionTerm className="gd-dt">Organization Name</DescriptionTerm>
            <DescriptionDetail :isLoading="isLoadingVendor">{{ item?.cas_organization?.name || '-' }}</DescriptionDetail>

            <DescriptionTerm className="gd-dt">Description</DescriptionTerm>
            <DescriptionDetail :isLoading="isLoadingVendor">
              <div
                ref="descriptionText"
                :class="['gd-line-clamp', { 'gd-line-clamp-expanded': isDescriptionExpanded }]"
              >
                {{ item?.description || '-' }}
              </div>
              <div v-if="showDescriptionToggle" class="flex items-center mt-1 cursor-pointer">
                <button class="ml-auto mr-1 text-xs font-semibold text-gray-900" @click="toggleDescriptionExpanded">Read {{ isDescriptionExpanded ? 'Less' : 'More' }}</button>
                <ChevronUpFilled v-if="isDescriptionExpanded" className="w-3 h-3 text-gray-900"/>
                <ChevronDownFilled v-else className="w-3 h-3 text-gray-900"/>
              </div>
            </DescriptionDetail>

            <DescriptionTerm className="gd-dt">Publisher Type</DescriptionTerm>
            <DescriptionDetail :isLoading="isLoadingVendor">{{ item?.is_external ? 'External' : 'Internal' }}</DescriptionDetail>

            <DescriptionTerm className="gd-dt">Vendor Source</DescriptionTerm>
            <DescriptionDetail :isLoading="isLoadingVendor">{{ item?.vendor_source || '-' }}</DescriptionDetail>

            <DescriptionTerm className="gd-dt">Vendor Content</DescriptionTerm>
            <DescriptionDetail :isLoading="isLoadingVendor">{{ vendorContent || '-' }}</DescriptionDetail>

            <DescriptionTerm className="gd-dt">Display Content</DescriptionTerm>
            <DescriptionDetail :isLoading="isLoadingVendor">{{ item?.display_content || '-' }}</DescriptionDetail>

            <DescriptionTerm className="gd-dt">Meta</DescriptionTerm>
            <DescriptionDetail :isLoading="isLoadingVendor">{{ item?.meta || '-' }}</DescriptionDetail>
          </DescriptionList>
        </div>

        <div class="flex-col w-1/2">
          <DescriptionList>
            <DescriptionTerm>Created Date</DescriptionTerm>
            <DescriptionDetail :isLoading="isLoadingVendor">{{ item?.created | formatDateCompact }}</DescriptionDetail>

            <DescriptionTerm className="gd-dt">Last Modified</DescriptionTerm>
            <DescriptionDetail :isLoading="isLoadingVendor">{{ item?.modified | formatDateCompact }}</DescriptionDetail>

            <DescriptionTerm className="gd-dt">New Premium Vendor Content ID</DescriptionTerm>
            <DescriptionDetail :isLoading="isLoadingVendor" v-if="item?.sync_vendor?.vendor_premium_id_ax !== '-1'">
              <Button
                v-if="item?.sync_vendor?.vendor_premium_id_ax === '0'"
                variant="outlined"
                color="alternative"
                size="sm"
                className="text-gray-900"
                :loading="requestBtnLoading"
                @click="requestPremiumId()"
              >
                {{ requestBtnLoading ? 'Loading...' : 'Request ID' }}
              </Button>
              <Label v-else-if="item?.sync_vendor?.vendor_premium_id_ax === '1'" variant="yellow">
                In Process
              </Label>
              <span v-else>{{ item?.sync_vendor?.vendor_premium_id_ax }}</span>
            </DescriptionDetail>

            <DescriptionTerm className="gd-dt">Approval Status</DescriptionTerm>
            <DescriptionDetail :isLoading="isLoadingVendor">
              <Label :variant="vendorStatus.labelVariant">{{ vendorStatus.text }}</Label>
            </DescriptionDetail>

            <DescriptionTerm className="gd-dt">Status</DescriptionTerm>
            <DescriptionDetail :isLoading="isLoadingVendor">
              <Label
                :variant="item?.is_active ? 'green' : 'gray'"
              >
                {{ item?.is_active ? 'Active' : 'Inactive' }}
              </Label>
            </DescriptionDetail>
          </DescriptionList>
        </div>
      </div>

    </div>

    <div class="w-full p-4 my-3 bg-white rounded-lg shadow-sm">
      <h4 class="text-xl font-bold text-gray-900">Publisher Agreement</h4>

      <div class="flex mt-2">
        <div class="flex-col w-1/2">
          <DescriptionList>
            <DescriptionTerm>Publisher ID AX</DescriptionTerm>
            <DescriptionDetail :isLoading="isLoadingVendor">
              {{ item?.sync_vendor ? item?.sync_vendor.vendor_id_ax : '-' }}
            </DescriptionDetail>

            <DescriptionTerm className="gd-dt">Formula ID</DescriptionTerm>
            <DescriptionDetail :isLoading="isLoadingItemRevenue">
              {{ itemRevenue ? itemRevenue.formula_id : '-' }}
            </DescriptionDetail>

            <DescriptionTerm className="gd-dt">Formula Name</DescriptionTerm>
            <DescriptionDetail :isLoading="isLoadingItemRevenue">
              {{ itemRevenue ? itemRevenue.formula_name : '-' }}
            </DescriptionDetail>

            <DescriptionTerm className="gd-dt">Payment Currency</DescriptionTerm>
            <DescriptionDetail :isLoading="isLoadingItemRevenue">
              {{ paymentCurrencyText }}
            </DescriptionDetail>

            <DescriptionTerm className="gd-dt">Default Fixed Amount</DescriptionTerm>
            <DescriptionDetail :isLoading="isLoadingItemRevenue">
              {{ itemRevenue ? itemRevenue.default_fixed_amount : '-' }}
            </DescriptionDetail>

            <DescriptionTerm className="gd-dt">Default Percentage Amount</DescriptionTerm>
            <DescriptionDetail :isLoading="isLoadingItemRevenue">
              {{
                itemRevenue
                ? `${toPercent(itemRevenue.default_percentage_amount)} %`
                : '-'
              }}
            </DescriptionDetail>
          </DescriptionList>
        </div>

        <div class="flex-col w-1/2">
          <DescriptionList>
            <DescriptionTerm>Status</DescriptionTerm>
            <DescriptionDetail :isLoading="isLoadingItemRevenue">
              <template v-if="!itemRevenue">-</template>
              <template v-else>
                <Label :variant="itemRevenue.is_active ? 'green' : 'gray'">
                  {{ itemRevenue.is_active ? 'Active' : 'Inactive' }}
                </Label>
              </template>
            </DescriptionDetail>
          </DescriptionList>
        </div>
      </div>

    </div>

    <div class="w-full p-4 my-3 bg-white rounded-lg shadow-sm">
      <h4 class="text-xl font-bold text-gray-900">Vendor Report Format</h4>

      <div class="flex mt-2">
        <div class="flex-col w-1/2">
          <DescriptionList>
            <DescriptionTerm className="gd-dt">Content Type</DescriptionTerm>
            <DescriptionDetail :isLoading="isLoadingVendorReportFormat">{{ vendorReportFormatContentType || '-' }}</DescriptionDetail>

            <DescriptionTerm className="gd-dt">Reason</DescriptionTerm>
            <DescriptionDetail :isLoading="isLoadingVendorReportFormat">
              <div
                ref="reasonText"
                :class="['gd-line-clamp', { 'gd-line-clamp-expanded': isReasonExpanded }]"
              >
                {{ vendorReportFormat?.status || '-' }}
              </div>
              <div v-if="showReasonToggle" class="flex items-center mt-1 cursor-pointer">
                <button class="ml-auto mr-1 text-xs font-semibold text-gray-900" @click="toggleReasonExpanded">Read {{ isReasonExpanded ? 'Less' : 'More' }}</button>
                <ChevronUpFilled v-if="isReasonExpanded" className="w-3 h-3 text-gray-900"/>
                <ChevronDownFilled v-else className="w-3 h-3 text-gray-900"/>
              </div>
            </DescriptionDetail>
          </DescriptionList>
        </div>

        <div class="flex-col w-1/2">
          <DescriptionList>
            <DescriptionTerm className="gd-dt">Status</DescriptionTerm>
            <DescriptionDetail :isLoading="isLoadingVendorReportFormat">
              <Label :variant="vendorReportFormat.is_active ? 'green' : 'gray'">
                {{ vendorReportFormat.is_active ? 'Active' : 'Inactive' }}
              </Label>
            </DescriptionDetail>
          </DescriptionList>
        </div>
      </div>
    </div>

    <div class="w-full p-4 my-3 bg-white rounded-lg shadow-sm">
      <h4 class="text-xl font-bold text-gray-900">Aggregation</h4>

      <div class="flex">
        <Table
          :headers="aggregateTableHeaders"
          :data="content"
          :rowKey="aggregateTableRowKeys"
          :isLoading="isLoadingContent"
          :withAction="false"
          :withPagination="false"
          className="w-full"
        >
          <template #countHeader>
            Quantity
          </template>
          <template #count="{ value }">
            <div>
              {{ value }}
            </div>
          </template>
        </Table>
      </div>
    </div>


  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import moment from 'moment';
import { EventBus } from '../../event-bus';

import HeaderBreadcrumbs from '@/components/HeaderBreadcrumbs';
import Button from '@/components/ui/Button';
import EditFilled from '@/components/icons/EditFilled';
import ChevronDownFilled from '@/components/icons/ChevronDownFilled';
import ChevronUpFilled from '@/components/icons/ChevronUpFilled';
import DescriptionList from '@/components/ui/DescriptionList';
import DescriptionTerm from '@/components/ui/DescriptionTerm';
import DescriptionDetail from '@/components/ui/DescriptionDetail';
import Label from '@/components/ui/Label';
import Skeleton from '@/components/ui/Skeleton';

import Table from '@/components/form/table';

import constant from '@/store/constant';
import defaultImage from '@/assets/img/default.jpg';

export default {
  name: 'VendorNewDetail',
  components: {
    HeaderBreadcrumbs,
    Button,
    EditFilled,
    ChevronDownFilled,
    ChevronUpFilled,
    DescriptionList,
    DescriptionTerm,
    DescriptionDetail,
    Label,
    Table,
    Skeleton
  },
  data() {
    return {
      imagePath: defaultImage,
      requestBtnLoading: false,
      seriesContent: [],
      optionsContent: {},
      aggregateTableHeaders: [
        { label: 'No', key: 'no' },
        { label: 'Content Type', key: 'item_type', class: 'w-5/12' },
        { label: 'Content Status', key: 'item_status', class: 'w-2/12'},
        { label: 'Quantity', key: 'count', class: 'w-5/12 text-right', slotHeader: 'countHeader' }
      ],
      aggregateTableRowKeys: [
        'item_type',
        'item_status',
        'count'
      ],
      aggregateTablePerPage: 20,
      isDescriptionExpanded: false,
      isReasonExpanded: false,
      isDescriptionTruncated: false,
      isReasonTruncated: false,
    }
  },
  computed: {
    ...mapState({
      item: ({ vendors }) => vendors.item,
      itemRevenue: ({ vendors }) => vendors.itemRevenue,
      isLoadingItemRevenue: ({ vendors }) => vendors.isLoadingItemRevenue,
      isLoadingVendor: ({ vendors }) => vendors.isLoading,
      itemVendor: ({ vendorScoopint }) => vendorScoopint.item,
      content: ({ dashboard }) => dashboard.Content,
      isLoadingContent: ({ dashboard }) => dashboard.isLoading,
      vendorReportFormat: ({ vendorReportFormat }) => vendorReportFormat.item,
      isLoadingVendorReportFormat: ({ vendorReportFormat }) => vendorReportFormat.isLoading,
    }),
    imageSrc() {
      if (this.item.icon_image_normal) {
        return `${constant.IMG_URL_STATIC_VENDOR.URL}${this.item.icon_image_normal}`;
      }

      return ''
    },
    vendorContent() {
      const content = [];

      if (this.item?.isContentBooks) {
        content.push('Is Content Books')
      }

      if (this.item?.isContentMagazines) {
        content.push('Is Content Magazines')
      }

      if (this.item?.isContentNewspapers) {
        content.push('Is Content Newspapers')
      }

      return content.join(', ')
    },
    vendorStatus() {
      return constant.VENDOR_STATUS_MAP[this.item?.vendor_status]
        ? constant.VENDOR_STATUS_MAP[this?.item.vendor_status] || constant.VENDOR_STATUS_MAP[7]
        : constant.VENDOR_STATUS_MAP[7]
    },
    paymentCurrencyText() {
      if (!this.itemRevenue?.payment_currency) return '-';
      
      if (!constant.PUBLISHER_PAYMENT_CURRENCY) return this.itemRevenue.payment_currency || '-';
      
      const paymentCurrencies = Object.entries(constant.PUBLISHER_PAYMENT_CURRENCY).map(
        (val) => ({
          text: val[0],
          value: val[1],
        }),
      );
      
      const currency = paymentCurrencies.find(c => c.value === this.itemRevenue.payment_currency);
      return currency ? currency.text : (this.itemRevenue.payment_currency || '-');
    },
    vendorReportFormatContentType () {
      const content = [];

      const {
        single = false,
        bundle = false,
        subscription = false,
        buffet = false
      } = this.vendorReportFormat || {};

      if (single) {
        content.push('Single');
      }

      if (bundle) {
        content.push('Bundle');
      }

      if (subscription) {
        content.push('Subscription');
      }

      if (buffet) {
        content.push('Buffet')
      }

      return content.join(', ')
    },
    showDescriptionToggle() {
      return this.item?.description && (this.isDescriptionTruncated || this.isDescriptionExpanded);
    },
    showReasonToggle() {
      return this.vendorReportFormat?.status && (this.isReasonTruncated || this.isReasonExpanded);
    }
  },
  mounted() {
    const vendorId = this.$route.params.id;
    this.fetchVendorsById({ id: vendorId });
    this.getVendorRevenue({ id: vendorId });
    this.fetchVendorFormatById({ id: vendorId });
    this.findVendorScoopintByID({ id: vendorId });
    this.actionSetLabelChart();
    this.actionFetchContent();

    // Check truncation after content loads
    this.$nextTick(() => {
      // Add a small delay to ensure CSS is fully applied
      setTimeout(() => {
        this.checkDescriptionTruncation();
        this.checkReasonTruncation();
      }, 100);
    });
  },
  watch: {
    content() {
      if (this.content.length > 0) {
        this.actionLoadChartContent();
      }
    },
    'item.description': {
      handler() {
        this.$nextTick(() => {
          setTimeout(() => {
            this.checkDescriptionTruncation();
          }, 50);
        });
      }
    },
    'vendorReportFormat.status': {
      handler() {
        this.$nextTick(() => {
          setTimeout(() => {
            this.checkReasonTruncation();
          }, 50);
        });
      }
    }
  },
  methods: {
    ...mapActions('vendors', ['fetchVendorsById', 'getVendorRevenue', 'postVendorsPremiumById']),
    ...mapActions('vendorScoopint', ['findVendorScoopintByID']),
    ...mapActions('dashboard', ['Content']),
    ...mapActions('vendorReportFormat', ['fetchVendorFormatById']),
    actionFetchContent() {
      const vendorId = this.$route.params.id;
      const payload = { vendor_id: vendorId };
      this.Content(payload);
    },
    actionLoadChartContent() {
      if (this.content) {
        this.seriesContent = this.content.map((e) => Number.parseInt(e.count, 10));
        this.optionsContent = {
          title: {
            text: 'Content',
            align: 'left',
          },
          legend: {
            position: 'bottom'
          },
          colors: [
            '#0060AF',
            '#ffec2e',
            '#ff06dc',
            '#0060af',
            '#f4975f',
            '#82a8f4',
          ],
          labels: this.content.map((e) => `${e.item_type} (${e.item_status})`),
          dataLabels: {
            enabled: true,
            formatter: (val) => {
              return `${Number.parseFloat(val).toFixed(2)}%`;
            },
          },
          plotOptions: {
            pie: {
              expandOnClick: true,
              donut: {
                labels: {
                  show: true,
                  value: {
                    formatter: (value) => {
                      const val = (value / 1).toFixed(0).replace('.', ',');
                      return val
                        .toString()
                        .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
                    },
                  },
                },
              },
            },
          },
        };
      }
    },
    actionSetLabelChart() {
      this.label_chart = [];
      const startMonth = moment()
        .subtract(this.showCountMonth, 'month')
        .format('YYYY-MM-DD');
      for (let index = 0; index < this.showCountMonth; index++) {
        const inMonth = moment(startMonth)
          .add(index, 'month')
          .format('YYYY-MM-DD');
        this.label_chart.push(
          moment(inMonth)
            .endOf('month')
            .format('YYYY-MM-DD'),
        );
      }
    },
    handleImageError(event) {
      event.target.src = this.imagePath;
    },
    toggleDescriptionExpanded () {
      this.isDescriptionExpanded = !this.isDescriptionExpanded;
      // No need to recheck truncation after toggle - the button should remain visible
    },
    toggleReasonExpanded () {
      this.isReasonExpanded = !this.isReasonExpanded;
      // No need to recheck truncation after toggle - the button should remain visible
    },
    checkDescriptionTruncation() {
      if (this.$refs.descriptionText && this.item?.description) {
        const element = this.$refs.descriptionText;
        // Temporarily remove expanded class to check original truncated state
        const wasExpanded = this.isDescriptionExpanded;
        if (wasExpanded) {
          element.classList.remove('gd-line-clamp-expanded');
        }
        
        // Force reflow to ensure CSS is applied
        element.offsetHeight;
        
        const isTruncated = element.scrollHeight > element.clientHeight;
        
        // Restore expanded state if it was expanded
        if (wasExpanded) {
          element.classList.add('gd-line-clamp-expanded');
        }
        
        this.isDescriptionTruncated = isTruncated;
      }
    },
    checkReasonTruncation() {
      if (this.$refs.reasonText && this.vendorReportFormat?.status) {
        const element = this.$refs.reasonText;
        // Temporarily remove expanded class to check original truncated state
        const wasExpanded = this.isReasonExpanded;
        if (wasExpanded) {
          element.classList.remove('gd-line-clamp-expanded');
        }
        
        // Force reflow to ensure CSS is applied
        element.offsetHeight;
        
        const isTruncated = element.scrollHeight > element.clientHeight;
        
        // Restore expanded state if it was expanded
        if (wasExpanded) {
          element.classList.add('gd-line-clamp-expanded');
        }
        
        this.isReasonTruncated = isTruncated;
      }
    },
    toPercent(num) {
      return (Number.parseFloat(num) * 100).toFixed(1);
    },
    async requestPremiumId() {
      this.requestBtnLoading = true;
      const id = this.$route.params.id;
      const payload = { id };

      try {
        const response = await this.postVendorsPremiumById(payload);
        this.requestBtnLoading = false;

        if (response.status === 200) {
          EventBus.$emit('show-toast', {
            message: 'Request Premium Success',
            type: 'success',
          });

          // Refresh vendor data to reflect the updated status
          this.fetchVendorsById({ id });
        }
      } catch (error) {
        this.requestBtnLoading = false;
        EventBus.$emit('show-toast', {
          message: 'Request Premium Failed',
          type: 'error',
        });
      }
    },
  },
}
</script>

<style scoped>
.gd-dt {
  margin-top: 16px;
}

.form-control-file {
  border-radius: 8px;
  height: 144px;
  width: 144px;
  object-fit: contain;
}

.gd-line-clamp {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  -webkit-line-clamp: 3;
}

.gd-line-clamp-expanded {
  -webkit-line-clamp: unset;
  display: block;
}
</style>
